import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Divider,
  Link,
  Alert,
  CircularProgress,
} from "@mui/material";
import { GoogleLogin } from "@react-oauth/google";
import GoogleIcon from "@mui/icons-material/Google";
import API_BASE_URL from "../../apiConfig";
import { TRANSPARENT_BLACK_08, PRIMARY_PINK, PRIMARY_PINK_DARK } from "../../constants";

function SignInModal({ open, onClose, onSignIn, onSwitchToSignUp, onSwitchToForgotPassword }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    // Validation
    if (!email) {
      setError("Email is required");
      return;
    }
    
    if (!password) {
      setError("Password is required");
      return;
    }

    setLoading(true);
    
    try {
      await onSignIn({ email, password });
    } catch (error) {
      console.error("Sign in error:", error);
      
      // Handle different types of errors
      if (error.response?.status === 401) {
        setError("Invalid email or password. Please check your credentials and try again.");
      } else if (error.response?.status === 404) {
        setError("User not found. Please check your email address.");
      } else if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else if (error.message) {
        setError(error.message);
      } else {
        setError("Failed to sign in. Please check your credentials and try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    setError("");
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          idToken: credentialResponse.credential,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 401) {
          throw new Error("Google authentication failed. Please try again.");
        } else if (response.status === 404) {
          throw new Error("Account not found. Please sign up first.");
        } else {
          throw new Error(errorData.error || "Google authentication failed");
        }
      }

      const userData = await response.json();
      await onSignIn({ googleUser: userData });
    } catch (error) {
      console.error("Google sign in error:", error);
      setError(error.message || "Google authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError("Google sign-in failed. Please try again.");
  };

  const handleClose = () => {
    setError("");
    setEmail("");
    setPassword("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
        <Typography variant="h5" component="div">
          Sign In
        </Typography>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              disabled={loading}
              useOneTap
            />
          </Box>
          
          <Divider sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              or
            </Typography>
          </Divider>

          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={(e) => {
                e.preventDefault();
                onSwitchToForgotPassword();
              }}
              sx={{ 
                cursor: "pointer",
                color: PRIMARY_PINK,
                fontWeight: 500,
                "&:hover": {
                  color: PRIMARY_PINK_DARK
                }
              }}
            >
              Forgot password?
            </Link>
          </Box>
          
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Don&apos;t have an account?{" "}
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={(e) => {
                  e.preventDefault();
                  onSwitchToSignUp();
                }}
                sx={{ 
                  cursor: "pointer",
                  color: PRIMARY_PINK,
                  fontWeight: 500,
                  "&:hover": {
                    color: PRIMARY_PINK_DARK
                  }
                }}
              >
                Sign up
              </Link>
            </Typography>
          </Box>
          
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button 
              type="button"
              onClick={handleClose} 
              fullWidth 
              variant="outlined"
              disabled={loading}
              sx={{
                color: "#000000",
                borderColor: "#000000",
                fontWeight: 600,
                fontSize: "1rem",
                textTransform: "none",
                "&:hover": {
                  borderColor: "#000000",
                  backgroundColor: TRANSPARENT_BLACK_08
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              fullWidth 
              variant="contained"
              disabled={loading}
              sx={{
                bgcolor: "#000000",
                color: "#FFFFFF",
                fontWeight: 600,
                fontSize: "1rem",
                textTransform: "none",
                "&:hover": {
                  bgcolor: "#333333"
                }
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : "Sign In"}
            </Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
      </DialogActions>
    </Dialog>
  );
}

SignInModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSignIn: PropTypes.func.isRequired,
  onSwitchToSignUp: PropTypes.func.isRequired,
  onSwitchToForgotPassword: PropTypes.func.isRequired,
};

export default SignInModal; 